"use client";

/**
 * Debug mode, `debug=1` on the page URL (every SDK's `debug: true`): the page traces what it
 * does so the app's developers can see where a tap, a message or a request stopped. Each line
 * goes to the console and, inside an app, to the app as a `log` bridge message, which every
 * SDK prints in its own log (a WKWebView's console reaches no native log).
 *
 * Traced: presses and clicks with what is under them, page visibility, every bridge message
 * both ways, every API request (method, path, status, time), chat turns, the basket drawer,
 * checkout, the session, and uncaught errors. Types, ids, labels and timings only: never a
 * token, a query string or what the shopper wrote.
 */
export type DebugLevel = "info" | "warn" | "error";
type Sink = (level: DebugLevel, message: string) => void;

let on = false;
let sink: Sink | null = null;

/** Set while rendering, before any child's effect can log: the same values every time. */
export function setDebug(enabled: boolean, toApp: Sink | null) {
	on = enabled;
	sink = enabled ? toApp : null;
}

export function debugEnabled() {
	return on;
}

function write(level: DebugLevel, parts: unknown[]) {
	if (!on) {
		return;
	}
	const message = parts
		.map((part) =>
			part instanceof Error
				? part.message
				: typeof part === "string"
					? part
					: JSON.stringify(part),
		)
		.join(" ")
		.slice(0, 2000);
	// biome-ignore lint/suspicious/noConsole: debug mode is for the app's developers
	console[level]("[hephon]", message);
	sink?.(level, message);
}

export function debugLog(...parts: unknown[]) {
	write("info", parts);
}

export function debugWarn(...parts: unknown[]) {
	write("warn", parts);
}

export function debugError(...parts: unknown[]) {
	write("error", parts);
}

/** A request's status and time, or why it failed. The path only: queries can hold tokens. */
export async function debugRequest(
	method: string,
	url: string,
	call: () => Promise<Response>,
): Promise<Response> {
	if (!on) {
		return call();
	}
	const path = url.replace(/^https?:\/\/[^/]+/, "").split("?")[0];
	const started = performance.now();
	const took = () => `${Math.round(performance.now() - started)} ms`;
	try {
		const response = await call();
		(response.ok ? debugLog : debugWarn)(
			`${method} ${path}: ${response.status} in ${took()}`,
		);
		return response;
	} catch (error) {
		debugError(`${method} ${path}: failed in ${took()}:`, error);
		throw error;
	}
}

/**
 * Traces input, visibility and uncaught errors while debug mode is on. A press with no click
 * after it is a tap the page never got to the end of; no press at all, a tap that never reached
 * the page.
 */
export function watchPage(): () => void {
	if (!on) {
		return () => {};
	}
	const describe = (event: Event) => {
		const target = event.target instanceof Element ? event.target : null;
		const control = target?.closest(
			"button, a, input, textarea, select, [role]",
		);
		const element = control ?? target;
		const label = (
			element?.getAttribute("aria-label") ??
			element?.textContent ??
			""
		)
			.replace(/\s+/g, " ")
			.trim()
			.slice(0, 40);
		const at =
			event instanceof MouseEvent
				? ` at ${Math.round(event.clientX)},${Math.round(event.clientY)}`
				: "";
		const disabled =
			control instanceof HTMLButtonElement && control.disabled
				? " (disabled)"
				: "";
		const inert = target?.closest("[inert]") ? " (inert)" : "";
		return `${element?.tagName.toLowerCase() ?? "nothing"}${label ? ` "${label}"` : ""}${disabled}${inert}${at}`;
	};
	const onPress = (event: PointerEvent) =>
		debugLog(`press (${event.pointerType})`, describe(event));
	const onClick = (event: MouseEvent) => debugLog("click", describe(event));
	const onCancel = (event: PointerEvent) =>
		debugLog(`press cancelled (${event.pointerType})`, describe(event));
	const onVisibility = () => debugLog(`page ${document.visibilityState}`);
	const onError = (event: ErrorEvent) =>
		debugError(
			"uncaught:",
			event.message,
			event.filename ? `(${event.filename}:${event.lineno})` : "",
		);
	const onRejection = (event: PromiseRejectionEvent) =>
		debugError("unhandled rejection:", event.reason);
	const onOnline = () => debugLog(navigator.onLine ? "online" : "offline");
	document.addEventListener("pointerdown", onPress, true);
	document.addEventListener("pointercancel", onCancel, true);
	document.addEventListener("click", onClick, true);
	document.addEventListener("visibilitychange", onVisibility);
	window.addEventListener("error", onError);
	window.addEventListener("unhandledrejection", onRejection);
	window.addEventListener("online", onOnline);
	window.addEventListener("offline", onOnline);
	debugLog(
		`debug on: page ${document.visibilityState}, ${navigator.onLine ? "online" : "offline"}, ${window.innerWidth}x${window.innerHeight}, ${navigator.userAgent}`,
	);
	return () => {
		document.removeEventListener("pointerdown", onPress, true);
		document.removeEventListener("pointercancel", onCancel, true);
		document.removeEventListener("click", onClick, true);
		document.removeEventListener("visibilitychange", onVisibility);
		window.removeEventListener("error", onError);
		window.removeEventListener("unhandledrejection", onRejection);
		window.removeEventListener("online", onOnline);
		window.removeEventListener("offline", onOnline);
	};
}
